import type { components } from '../endpoint/Schema';

type AppColorDto = components['schemas']['AppColorDto'];

/**
 * Applies tenant-specific colors to CSS custom properties
 * @param appColors Array of tenant app colors
 */
export const applyTenantColors = (appColors: AppColorDto[]): void => {
  const root = document.documentElement;

  // Apply primary color variations
  const primaryColor = appColors.find((c) => c.type === 'P400');
  if (primaryColor?.value) {
    root.style.setProperty('--color-primary', primaryColor.value);
    root.style.setProperty('--primary-color', primaryColor.value);
  }

  const primaryDarkColor = appColors.find((c) => c.type === 'P600');
  if (primaryDarkColor?.value) {
    root.style.setProperty('--color-primary-dark', primaryDarkColor.value);
  }

  const primaryVeryDarkColor = appColors.find((c) => c.type === 'P500');
  if (primaryVeryDarkColor?.value) {
    root.style.setProperty(
      '--color-primary-very-dark',
      primaryVeryDarkColor.value,
    );
  }

  const primaryMediumColor = appColors.find((c) => c.type === 'P300');
  if (primaryMediumColor?.value) {
    root.style.setProperty('--color-primary-medium', primaryMediumColor.value);
  }

  const primaryLightColor = appColors.find((c) => c.type === 'P200');
  if (primaryLightColor?.value) {
    root.style.setProperty('--color-primary-light', primaryLightColor.value);
    root.style.setProperty(
      '--color-primary-very-light',
      primaryLightColor.value,
    );
  }

  // Apply secondary color variations
  const secondaryColor = appColors.find((c) => c.type === 'S400');
  if (secondaryColor?.value) {
    root.style.setProperty('--color-secondary', secondaryColor.value);
  }

  const secondaryLightColor = appColors.find((c) => c.type === 'S200');
  if (secondaryLightColor?.value) {
    root.style.setProperty(
      '--color-secondary-light',
      secondaryLightColor.value,
    );
  }

  // Apply surface colors
  const surfaceColor = appColors.find((c) => c.type === 'Sf');
  if (surfaceColor?.value) {
    root.style.setProperty('--color-surface', surfaceColor.value);
  }

  const lightSurfaceColor = appColors.find((c) => c.type === 'LSf');
  if (lightSurfaceColor?.value) {
    root.style.setProperty('--color-surface-light', lightSurfaceColor.value);
  }

  // Apply accent colors
  const accentLightColor = appColors.find((c) => c.type === 'Al');
  if (accentLightColor?.value) {
    root.style.setProperty('--color-accent-light', accentLightColor.value);
  }

  const lightAccentLightColor = appColors.find((c) => c.type === 'LAl');
  if (lightAccentLightColor?.value) {
    root.style.setProperty(
      '--color-accent-light-alt',
      lightAccentLightColor.value,
    );
  }

  // Apply dark/night colors
  const darkColor = appColors.find((c) => c.type === 'Dn');
  if (darkColor?.value) {
    root.style.setProperty('--color-dark', darkColor.value);
  }

  const lightDarkColor = appColors.find((c) => c.type === 'LDn');
  if (lightDarkColor?.value) {
    root.style.setProperty('--color-dark-light', lightDarkColor.value);
  }
};

/**
 * Clears all tenant-specific color CSS custom properties
 */
export const clearTenantColors = (): void => {
  const root = document.documentElement;

  root.style.removeProperty('--color-primary');
  root.style.removeProperty('--primary-color');
  root.style.removeProperty('--color-primary-dark');
  root.style.removeProperty('--color-primary-very-dark');
  root.style.removeProperty('--color-primary-medium');
  root.style.removeProperty('--color-primary-light');
  root.style.removeProperty('--color-primary-very-light');
  root.style.removeProperty('--color-secondary');
  root.style.removeProperty('--color-secondary-light');
  root.style.removeProperty('--color-surface');
  root.style.removeProperty('--color-surface-light');
  root.style.removeProperty('--color-accent-light');
  root.style.removeProperty('--color-accent-light-alt');
  root.style.removeProperty('--color-dark');
  root.style.removeProperty('--color-dark-light');
};
